import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ArrowRight, ArrowLeft, User, Phone, Mail, 
  Globe, DollarSign, Monitor, Calendar, HelpCircle,
  CheckCircle, Download, MessageCircle, Send, Loader2
} from 'lucide-react';
import { CountryPhoneInput, COUNTRIES } from './CountryPhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Google Meet SVG Icon
const GoogleMeetIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 10.5V13.5M12 10.5L15.5 8M12 10.5L8.5 8M12 13.5L15.5 16M12 13.5L8.5 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 7.5C3 5.29086 4.79086 3.5 7 3.5H17C19.2091 3.5 21 5.29086 21 7.5V16.5C21 18.7091 19.2091 20.5 17 20.5H7C4.79086 20.5 3 18.7091 3 16.5V7.5Z" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M21 9L17 12L21 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const STEPS = [
  { id: 1, title: 'Your Details', icon: User },
  { id: 2, title: 'Language', icon: Globe },
  { id: 3, title: 'Trading Setup', icon: DollarSign },
  { id: 4, title: 'Platform', icon: Monitor },
  { id: 5, title: 'Timeline', icon: Calendar },
  { id: 6, title: 'Tester', icon: Download },
  { id: 7, title: 'Question', icon: HelpCircle },
];

export const BookCallModal = ({ isOpen, onClose, embedded = false }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [leadId, setLeadId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // 'qualified' | 'exploring'
  
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    countryCode: 'IN',
    email: '',
    language: '',
    tradingLocation: '', // 'own' | 'prop' | 'both'
    capitalAbove3k: '', // 'yes' | 'no' | 'amount'
    capitalAmount: '',
    propAccountSize: '',
    platform: '',
    timeline: '',
    triedTester: '',
    mainQuestion: '',
    website: '', // Honeypot field - leave empty
  });

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setLeadId(null);
      setResult(null);
      setFormData({
        name: '', whatsapp: '', countryCode: 'IN', email: '',
        language: '', tradingLocation: '', capitalAbove3k: '',
        capitalAmount: '', propAccountSize: '', platform: '',
        timeline: '', triedTester: '', mainQuestion: '', website: '',
      });
    }
  }, [isOpen]);

  const [saveError, setSaveError] = useState('');

  const saveLead = async (data, isUpdate = false) => {
    try {
      setSaving(true);
      const endpoint = isUpdate && leadId 
        ? `${API_URL}/api/calls/leads/${leadId}`
        : `${API_URL}/api/calls/leads`;
      
      // Get dial code from country code
      const country = COUNTRIES.find(c => c.code === data.countryCode);
      const dialCode = country ? country.dial : '+91';
      
      const response = await fetch(endpoint, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          phone: `${dialCode}${data.whatsapp}`,
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (!isUpdate) {
          setLeadId(result.lead_id);
        }
        setSaveError('');
        return result;
      }
      const err = await response.json().catch(() => ({}));
      setSaveError(response.status < 500 && typeof err.detail === 'string' ? err.detail : 'Could not save your details. Please check them and try again.');
    } catch (error) {
      console.error('Error saving lead:', error);
      setSaveError('Connection problem. Please check your internet and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';

    // Save lead on Step 1, then update the same record with every later answer
    // Never move on if the save failed, so no lead is silently lost
    if (currentStep === 1 && formData.name && formData.whatsapp && formData.email) {
      if (!(await saveLead({ ...formData, timezone }))) return;
    }
    if (currentStep > 1 && leadId) {
      if (!(await saveLead({ ...formData, timezone, last_step: currentStep, completed: currentStep === 7 }, true))) return;
    }

    if (currentStep === 5 && formData.timeline === 'exploring') {
      setResult('exploring');
      return;
    }

    if (currentStep === 7) {
      setResult('qualified');
      return;
    }

    setCurrentStep(prev => Math.min(prev + 1, 7));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.whatsapp && formData.email && 
               /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
      case 2:
        return formData.language;
      case 3:
        if (!formData.tradingLocation) return false;
        if (formData.tradingLocation === 'own' || formData.tradingLocation === 'both') {
          if (!formData.capitalAbove3k) return false;
          if (formData.capitalAbove3k === 'amount' && !formData.capitalAmount) return false;
        }
        if (formData.tradingLocation === 'prop' || formData.tradingLocation === 'both') {
          if (!formData.propAccountSize) return false;
        }
        return true;
      case 4:
        return formData.platform;
      case 5:
        return formData.timeline;
      case 6:
        return formData.triedTester;
      case 7:
        return true; // Question is optional
      default:
        return false;
    }
  };

  const OptionButton = ({ selected, onClick, children, className = '', testId }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full p-4 rounded-xl border text-left transition-all ${
        selected 
          ? 'border-[#7c3aed] bg-[#7c3aed]/10 text-white' 
          : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20 hover:bg-white/10'
      } ${className}`}
    >
      {children}
    </button>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your name"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#7c3aed] focus:outline-none"
                data-testid="book-call-name"
              />
            </div>
            <div>
              <CountryPhoneInput
                countryCode={formData.countryCode}
                setCountryCode={(code) => setFormData(prev => ({ ...prev, countryCode: code }))}
                phone={formData.whatsapp}
                setPhone={(phone) => setFormData(prev => ({ ...prev, whatsapp: phone }))}
                label="WhatsApp Number"
                testIdPrefix="book-call"
              />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="your@email.com"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#7c3aed] focus:outline-none"
                data-testid="book-call-email"
              />
            </div>
            <p className="text-white/40 text-xs">
              We'll use these details to schedule your call and send reminders and follow-ups by email and WhatsApp.
            </p>
            {/* Honeypot: fully hidden with a non-autofill name, so phones/password managers never fill it */}
            <div style={{ display: 'none' }} aria-hidden="true">
              <input
                type="text"
                name="mk_hp_ignore"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                autoComplete="new-password"
                tabIndex={-1}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-3">
            <p className="text-white/60 text-sm mb-4">Which language are you comfortable with on the call?</p>
            <OptionButton
              selected={formData.language === 'hindi'}
              onClick={() => setFormData(prev => ({ ...prev, language: 'hindi' }))}
              testId="call-language-hindi"
            >
              <span className="font-semibold">Hindi</span>
            </OptionButton>
            <OptionButton
              selected={formData.language === 'english'}
              onClick={() => setFormData(prev => ({ ...prev, language: 'english' }))}
              testId="call-language-english"
            >
              <span className="font-semibold">English</span>
            </OptionButton>
            <OptionButton
              selected={formData.language === 'both'}
              onClick={() => setFormData(prev => ({ ...prev, language: 'both' }))}
              testId="call-language-both"
            >
              <span className="font-semibold">Both (Hinglish)</span>
            </OptionButton>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <p className="text-white/60 text-sm mb-4">Where will you run the algo?</p>
            <div className="space-y-3">
              <OptionButton
                selected={formData.tradingLocation === 'own'}
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  tradingLocation: 'own',
                  propAccountSize: ''
                }))}
              >
                <span className="font-semibold">Own capital</span>
              </OptionButton>
              <OptionButton
                selected={formData.tradingLocation === 'prop'}
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  tradingLocation: 'prop',
                  capitalAbove3k: '',
                  capitalAmount: ''
                }))}
              >
                <span className="font-semibold">Prop firm</span>
              </OptionButton>
              <OptionButton
                selected={formData.tradingLocation === 'both'}
                onClick={() => setFormData(prev => ({ ...prev, tradingLocation: 'both' }))}
              >
                <span className="font-semibold">Both</span>
              </OptionButton>
            </div>

            {/* Capital question for own capital */}
            {(formData.tradingLocation === 'own' || formData.tradingLocation === 'both') && (
              <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/60 text-sm mb-3">Is your trading capital more than $3,000?</p>
                <div className="space-y-2">
                  <OptionButton
                    selected={formData.capitalAbove3k === 'yes'}
                    onClick={() => setFormData(prev => ({ ...prev, capitalAbove3k: 'yes', capitalAmount: '' }))}
                    className="py-3"
                  >
                    Yes
                  </OptionButton>
                  <OptionButton
                    selected={formData.capitalAbove3k === 'no'}
                    onClick={() => setFormData(prev => ({ ...prev, capitalAbove3k: 'no', capitalAmount: '' }))}
                    className="py-3"
                  >
                    No
                  </OptionButton>
                  <OptionButton
                    selected={formData.capitalAbove3k === 'amount'}
                    onClick={() => setFormData(prev => ({ ...prev, capitalAbove3k: 'amount' }))}
                    className="py-3"
                  >
                    Enter amount
                  </OptionButton>
                  {formData.capitalAbove3k === 'amount' && (
                    <input
                      type="number"
                      value={formData.capitalAmount}
                      onChange={(e) => setFormData(prev => ({ ...prev, capitalAmount: e.target.value }))}
                      placeholder="Enter amount in USD"
                      className="w-full px-4 py-3 mt-2 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#7c3aed] focus:outline-none"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Prop account size */}
            {(formData.tradingLocation === 'prop' || formData.tradingLocation === 'both') && (
              <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/60 text-sm mb-3">Total prop account size?</p>
                <div className="space-y-2">
                  {['Under $50k', '$50k–100k', '$100k+', 'Not bought yet'].map((size) => (
                    <OptionButton
                      key={size}
                      selected={formData.propAccountSize === size}
                      onClick={() => setFormData(prev => ({ ...prev, propAccountSize: size }))}
                      className="py-3"
                    >
                      {size}
                    </OptionButton>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-3">
            <p className="text-white/60 text-sm mb-4">Which platform?</p>
            <OptionButton
              selected={formData.platform === 'MT5'}
              onClick={() => setFormData(prev => ({ ...prev, platform: 'MT5' }))}
            >
              <span className="font-semibold">MT5</span>
              <p className="text-white/40 text-sm mt-1">MetaTrader 5</p>
            </OptionButton>
            <OptionButton
              selected={formData.platform === 'NinjaTrader 8'}
              onClick={() => setFormData(prev => ({ ...prev, platform: 'NinjaTrader 8' }))}
            >
              <span className="font-semibold">NinjaTrader 8</span>
              <p className="text-white/40 text-sm mt-1">For futures trading</p>
            </OptionButton>
            <OptionButton
              selected={formData.platform === 'Not set up yet'}
              onClick={() => setFormData(prev => ({ ...prev, platform: 'Not set up yet' }))}
            >
              <span className="font-semibold">Not set up yet</span>
              <p className="text-white/40 text-sm mt-1">We'll help you get started</p>
            </OptionButton>
          </div>
        );

      case 5:
        return (
          <div className="space-y-3">
            <p className="text-white/60 text-sm mb-4">When do you want to start?</p>
            <OptionButton
              selected={formData.timeline === 'this_week'}
              onClick={() => setFormData(prev => ({ ...prev, timeline: 'this_week' }))}
            >
              <span className="font-semibold">This week</span>
            </OptionButton>
            <OptionButton
              selected={formData.timeline === 'this_month'}
              onClick={() => setFormData(prev => ({ ...prev, timeline: 'this_month' }))}
            >
              <span className="font-semibold">This month</span>
            </OptionButton>
            <OptionButton
              selected={formData.timeline === 'exploring'}
              onClick={() => setFormData(prev => ({ ...prev, timeline: 'exploring' }))}
            >
              <span className="font-semibold">Just exploring</span>
            </OptionButton>
          </div>
        );

      case 6:
        return (
          <div className="space-y-3">
            <p className="text-white/60 text-sm mb-4">Have you tried the free tester?</p>
            <OptionButton
              selected={formData.triedTester === 'yes_worked'}
              onClick={() => setFormData(prev => ({ ...prev, triedTester: 'yes_worked' }))}
            >
              <span className="font-semibold">Yes, it worked</span>
            </OptionButton>
            <OptionButton
              selected={formData.triedTester === 'couldnt_get_working'}
              onClick={() => setFormData(prev => ({ ...prev, triedTester: 'couldnt_get_working' }))}
            >
              <span className="font-semibold">Couldn't get it working</span>
              <p className="text-white/40 text-sm mt-1">We'll help you set it up on the call</p>
            </OptionButton>
            <OptionButton
              selected={formData.triedTester === 'not_yet'}
              onClick={() => setFormData(prev => ({ ...prev, triedTester: 'not_yet' }))}
            >
              <span className="font-semibold">Not yet</span>
            </OptionButton>
          </div>
        );

      case 7:
        return (
          <div className="space-y-4">
            <p className="text-white/60 text-sm mb-4">Your main question for the call (optional)</p>
            <textarea
              value={formData.mainQuestion}
              onChange={(e) => setFormData(prev => ({ ...prev, mainQuestion: e.target.value }))}
              placeholder="What would you like to know?"
              rows={4}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#7c3aed] focus:outline-none resize-none"
              data-testid="book-call-question"
            />
          </div>
        );

      default:
        return null;
    }
  };

  const renderResult = () => {
    if (result === 'exploring') {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-8"
        >
          <div className="w-16 h-16 rounded-full bg-[#06b6d4]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#06b6d4]" />
          </div>
          <h3 className="font-heading font-bold text-2xl text-white mb-3">
            You're early, and that's fine
          </h3>
          <p className="text-white/60 mb-8">
            Start here, and book your call when you're ready to start.
          </p>
          <div className="space-y-3">
            <a
              href="/orb-algo#tester"
              className="flex items-center justify-center gap-2 w-full py-4 rounded-xl bg-[#7c3aed]/20 border border-[#7c3aed]/30 text-white font-medium hover:bg-[#7c3aed]/30 transition-all"
              onClick={onClose}
            >
              <Download size={18} />
              Download the free tester
            </a>
            <a
              href="https://t.me/+9yeoU7R5h3k2NDVl"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-4 rounded-xl bg-[#0088cc]/20 border border-[#0088cc]/30 text-white font-medium hover:bg-[#0088cc]/30 transition-all"
            >
              <Send size={18} />
              See live results on Telegram
            </a>
            <a
              href="https://wa.me/message/LMGG2O4MVCCTG1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-4 rounded-xl bg-[#25D366]/20 border border-[#25D366]/30 text-white font-medium hover:bg-[#25D366]/30 transition-all"
            >
              <MessageCircle size={18} />
              Chat on WhatsApp
            </a>
          </div>
        </motion.div>
      );
    }

    if (result === 'qualified') {
      // Detect visitor's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
      const WHATSAPP_NUMBER = "917223863486";
      
      // Generate WhatsApp message based on language
      const message = formData.language === 'hindi'
        ? `नमस्ते साहिल, मैंने monkalgo.com पर आपके साथ Google Meet कॉल बुक की है। क्या हम ऐसा समय तय कर सकते हैं जो हम दोनों के लिए सही हो? नाम: ${formData.name}. मेरा टाइम ज़ोन: ${timezone}.`
        : `Hi Sahil, I have booked a Google Meet call with you on monkalgo.com. Can we decide a time that works best for both of us? Name: ${formData.name}. My time zone: ${timezone}.`;
      
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
      
      const handleWhatsAppClick = async () => {
        // Track that WhatsApp was opened
        if (leadId) {
          try {
            await fetch(`${API_URL}/api/calls/leads/${leadId}/whatsapp-opened`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            });
          } catch (error) {
            console.error('Error tracking WhatsApp click:', error);
          }
        }
        // Open WhatsApp
        window.open(whatsappUrl, '_blank');
      };
      
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-8"
        >
          <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#10B981]" />
          </div>
          <h3 className="font-heading font-bold text-2xl text-white mb-3">
            You qualify.
          </h3>
          <p className="text-white/60 mb-8 max-w-sm mx-auto">
            One last step: send us a WhatsApp message to schedule your Google Meet time with Sahil.
          </p>
          <button
            onClick={handleWhatsAppClick}
            className="w-full py-4 rounded-xl bg-[#25D366] text-white font-semibold hover:bg-[#20BD5A] transition-all flex items-center justify-center gap-3 shadow-lg shadow-[#25D366]/30"
            data-testid="schedule-whatsapp-btn"
          >
            <MessageCircle size={20} />
            Schedule on WhatsApp
          </button>
          <p className="text-white/40 text-xs mt-4">
            WhatsApp not opening? Message us at <span className="text-white/60">+91 72238 63486</span>
          </p>
        </motion.div>
      );
    }

    return null;
  };

  const content = (
    <div className={embedded ? '' : 'fixed inset-0 z-[100] flex items-center justify-center'}>
      {!embedded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />
      )}
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className={`relative w-full ${embedded ? '' : 'max-w-lg mx-4 max-h-[90vh] overflow-y-auto'} bg-[#0a0a12] rounded-2xl border border-white/10 shadow-2xl`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a0a12] p-6 pb-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7c3aed]/20 to-[#06b6d4]/20 flex items-center justify-center">
                <GoogleMeetIcon className="w-5 h-5 text-[#7c3aed]" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-white">Book a Call with Sahil</h2>
                <p className="text-white/40 text-xs">on Google Meet</p>
              </div>
            </div>
            {!embedded && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="book-call-close"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            )}
          </div>

          {/* Progress bar */}
          {!result && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Step {currentStep} of 7: {STEPS[currentStep - 1]?.title}</span>
                <span className="text-[#7c3aed]">{Math.round((currentStep / 7) * 100)}%</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#7c3aed] to-[#06b6d4]"
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentStep / 7) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {result ? renderResult() : (
            <>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              {saveError && (
                <p className="mt-6 text-red-400 text-sm" data-testid="call-form-save-error">{saveError}</p>
              )}
              <div className="flex gap-3 mt-8">
                {currentStep > 1 && (
                  <button
                    onClick={handleBack}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-white/70 hover:bg-white/5 transition-all"
                  >
                    <ArrowLeft size={16} />
                    Back
                  </button>
                )}
                <button
                  onClick={handleNext}
                  disabled={!canProceed() || saving}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all ${
                    canProceed() && !saving
                      ? 'bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white hover:opacity-90'
                      : 'bg-white/10 text-white/30 cursor-not-allowed'
                  }`}
                  data-testid="book-call-next"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : currentStep === 7 ? (
                    'See available times'
                  ) : (
                    <>
                      Continue
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer note */}
        {!result && currentStep === 7 && (
          <div className="px-6 pb-6">
            <p className="text-white/30 text-xs text-center">
              Before you book: no free trials or discounts, no account management, no guaranteed returns, no monthly plan.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <AnimatePresence>
      {isOpen && content}
    </AnimatePresence>
  );
};

export default BookCallModal;
